// -----------------------------------------------------------
// FloraIQ - Catalogo das regras de qualidade realmente implementadas
// -----------------------------------------------------------
// Espelha a view qualidade.v_validation_issues_sql
// (database/migrations/015_data_views_quality.sql). Sao as unicas regras que
// hoje geram inconsistencias reais no endpoint GET /quality/issues. Se a view
// ganhar ou perder regra, este catalogo precisa acompanhar.

export type QualityRuleGroup = "arboreo" | "fontes" | "rrr" | "nuvem";

export interface QualityRuleDef {
  code: string;
  name: string;
  description: string;
  /** Severidade gravada pela view (rotulo de UI, igual ao mapper). */
  severity: "crítica" | "alta" | "média";
  group: QualityRuleGroup;
  /** Tabela onde o registro inconsistente vive. */
  entity: string;
}

export const QUALITY_RULE_GROUP_LABELS: Record<QualityRuleGroup, string> = {
  arboreo: "Cadastro Arbóreo",
  fontes: "Fontes e Evidências",
  rrr: "RRR e Autorizações",
  nuvem: "Nuvem de Pontos",
};

export const QUALITY_RULE_GROUP_ICONS: Record<QualityRuleGroup, string> = {
  arboreo: "ri-plant-line",
  fontes: "ri-folder-info-line",
  rrr: "ri-shield-check-line",
  nuvem: "ri-stack-line",
};

export const QUALITY_RULES: QualityRuleDef[] = [
  {
    code: "TREE_SPATIAL_UNIT_REQUIRED",
    name: "Unidade espacial LADM vinculada",
    description: "Exemplar arbóreo deve estar vinculado a uma unidade espacial LADM.",
    severity: "média",
    group: "arboreo",
    entity: "cta.tree_individuals",
  },
  {
    code: "REMOVED_TREE_HISTORY_REQUIRED",
    name: "Histórico de exemplar removido",
    description: "Exemplar removido ou suprimido deve ter evento histórico correspondente.",
    severity: "crítica",
    group: "arboreo",
    entity: "cta.tree_individuals",
  },
  {
    code: "TREE_SOURCE_REQUIRED",
    name: "Fonte rastreável",
    description: "Exemplar arbóreo deve ter fonte rastreável em vistoria ou evento.",
    severity: "alta",
    group: "fontes",
    entity: "cta.tree_individuals",
  },
  {
    code: "HIGH_RISK_PHOTO_REQUIRED",
    name: "Foto de evidência para risco alto",
    description: "Avaliação de risco alto ou crítico deve ter foto de evidência vinculada.",
    severity: "alta",
    group: "fontes",
    entity: "cta.tree_risk_assessments",
  },
  {
    code: "SUPPRESSION_AUTH_REQUIRED",
    name: "Autorização de supressão",
    description: "Evento de supressão deve ter documento, fonte ou autorização RRR associada.",
    severity: "crítica",
    group: "rrr",
    entity: "cta.tree_events",
  },
  {
    code: "INTERVENTION_RESPONSIBLE_REQUIRED",
    name: "Responsável pela intervenção",
    description: "Intervenção executada deve ter parte responsável.",
    severity: "alta",
    group: "rrr",
    entity: "operacional.interventions",
  },
  {
    code: "PROTECTED_SPECIES_RESTRICTION_REQUIRED",
    name: "Restrição de espécie protegida",
    description: "Espécie protegida deve ter restrição ou condicionante RRR associada.",
    severity: "alta",
    group: "rrr",
    entity: "cta.tree_individuals",
  },
  {
    code: "POINT_CLOUD_METADATA_REQUIRED",
    name: "Metadados da nuvem de pontos",
    description: "Nuvem de pontos deve ter metadados mínimos de aquisição, sensor, equipamento ou cobertura.",
    severity: "média",
    group: "nuvem",
    entity: "gis.point_cloud_assets",
  },
];

export const QUALITY_RULES_BY_CODE: Record<string, QualityRuleDef> = Object.fromEntries(
  QUALITY_RULES.map((r) => [r.code, r])
);

export function qualityRuleCodesOfGroup(group: QualityRuleGroup): string[] {
  return QUALITY_RULES.filter((r) => r.group === group).map((r) => r.code);
}

/** Opcoes de severidade para filtros (rotulos iguais aos do mapper). */
export const QUALITY_SEVERITY_OPTIONS = [
  { value: "crítica", label: "Crítica" },
  { value: "alta", label: "Alta" },
  { value: "média", label: "Média" },
  { value: "baixa", label: "Baixa" },
];

/** Status de validacao campo x 3D devolvidos por /point-cloud/tree-metrics. */
export const TREE_METRIC_3D_STATUS_LABELS: Record<string, string> = {
  validado: "Validado",
  divergente: "Divergente",
  sem_3d: "Sem 3D",
};
